////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

/**
 * ════════════════════════════════════════════════════════════════════════════════
 * REGISTER PEA - REGISTRATION PRESENTATION (POD/PEA PATTERN)
 * ════════════════════════════════════════════════════════════════════════════════
 *
 * ARCHITECTURAL ROLE: Pure UI component rendering comprehensive registration form
 *
 * I[R{DVT}]O PATTERN:
 *   INPUT: Registration form props from Pod (formData, validation callbacks)
 *   REQUIREMENTS: Multi-field form with real-time validation, password strength meter
 *   OUTPUT: Accessible registration UI with terms acceptance and newsletter opt-in
 *
 * FEATURES: Password strength visualization, real-time email validation, GDPR-compliant consent
 */

import React, { useState } from 'react';
import styles from './Bitsbound_Kings_Customer_Auth_SRC_Register_Lobby_Pea.module.css';

interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
  companyName: string;
  firstName: string;
  lastName: string;
  acceptTerms: boolean;
  subscribeNewsletter: boolean;
}

interface RegisterPeaProps {
  formData: RegisterFormData;
  isLoading: boolean;
  errors?: { [key: string]: string };
  onFieldChange: (field: keyof RegisterFormData, value: string | boolean) => void;
  onSubmit: (e: React.FormEvent) => void;
  onLoginClick: () => void;
  onGoogleSignUp?: () => void;
  onGitHubSignUp?: () => void;
  // Email verification state props
  verificationPending?: boolean;      // DEFINITION: Whether email verification is required
  registeredEmail?: string;           // DEFINITION: Email address needing verification
  onResendVerification?: () => void;  // DEFINITION: Callback to resend verification email
}

const Pea_Lobby_Register_SRC_Auth_Customer_Kings: React.FC<RegisterPeaProps> = ({
  formData,
  isLoading,
  errors = {},
  onFieldChange,
  onSubmit,
  onLoginClick,
  onGoogleSignUp,
  onGitHubSignUp,
  verificationPending = false,
  registeredEmail = '',
  onResendVerification
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const calculatePasswordStrength = (password: string): number => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) strength++;
    if (password.match(/\d/)) strength++;
    if (password.match(/[^a-zA-Z\d]/)) strength++;
    return strength;
  };

  const handlePasswordChange = (value: string) => {
    onFieldChange('password', value);
    setPasswordStrength(calculatePasswordStrength(value));
  };

  const getPasswordStrengthLabel = () => {
    switch (passwordStrength) {
      case 0: return 'Very Weak';
      case 1: return 'Weak';
      case 2: return 'Fair';
      case 3: return 'Good';
      case 4: return 'Strong';
      default: return '';
    }
  };

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 0: return '#dc2626';
      case 1: return '#f59e0b';
      case 2: return '#eab308';
      case 3: return '#84cc16';
      case 4: return '#22c55e';
      default: return '#e5e7eb';
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoading) {
      onSubmit(e);
    }
  };

  // ════════════════════════════════════════════════════════════════════════════════
  // EMAIL VERIFICATION PENDING UI
  // ════════════════════════════════════════════════════════════════════════════════
  if (verificationPending) {
    return (
      <div className={styles.registerContainer}>
        <div className={styles.registerCard}>
          <div className={styles.logoSection}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#20b2aa" strokeWidth="2" style={{ margin: '0 auto' }}>
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <h1 className={styles.title}>Check Your Email</h1>
            <p className={styles.subtitle}>
              We've sent a verification link to:
            </p>
            <p style={{
              color: '#20b2aa',
              fontSize: '18px',
              fontWeight: '600',
              marginTop: '8px',
              marginBottom: '24px'
            }}>
              {registeredEmail}
            </p>
            <p style={{
              color: '#b0b0b0',
              fontSize: '14px',
              lineHeight: '1.6',
              marginBottom: '24px'
            }}>
              Click the link in the email to verify your account and get started with BitsBound.
              The link will expire in 24 hours.
            </p>
          </div>

          <div style={{ textAlign: 'center' }}>
            <p style={{ color: '#888', fontSize: '14px', marginBottom: '16px' }}>
              Didn't receive the email?
            </p>
            <button
              type="button"
              onClick={onResendVerification}
              disabled={isLoading}
              className={styles.submitButton}
              style={{
                background: 'transparent',
                border: '1px solid #20b2aa',
                marginBottom: '16px'
              }}
            >
              {isLoading ? 'Sending...' : 'Resend Verification Email'}
            </button>
            <p style={{ color: '#666', fontSize: '12px' }}>
              Also check your spam folder
            </p>
          </div>

          <div className={styles.footer}>
            <p className={styles.footerText}>
              Already verified?{' '}
              <button
                type="button"
                onClick={onLoginClick}
                className={styles.link}
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.registerContainer}>
      <div className={styles.registerCard}>
        <div className={styles.logoSection}>
          {/* T_HOMEPAGE_LINK_2026_01_02: Logo links back to homepage */}
          <a href="https://www.bitsbound.com" title="Back to BitsBound homepage">
            <img
              src="https://www.bitsbound.com/no-background/Bitsbound_Shared_Shared_Components_BbLogo_NoBackground.svg"
              alt="BitsBound"
              className={styles.logo}
            />
          </a>
          <h1 className={styles.title}>Create Your Account</h1>
          <p className={styles.subtitle}>
            First contract free <span style={{ color: '#b565b5', fontWeight: 600 }}>(1.5M credits on signup)</span>. No credit card required.
          </p>
        </div>

        {(onGoogleSignUp || onGitHubSignUp) && (
          <>
            <div>
              {onGoogleSignUp && (
                <button
                  type="button"
                  onClick={onGoogleSignUp}
                  className={styles.signUpButton}
                  disabled={isLoading}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </button>
              )}
              {onGitHubSignUp && (
                <button
                  type="button"
                  onClick={onGitHubSignUp}
                  className={styles.signUpButton}
                  disabled={isLoading}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                  Continue with GitHub
                </button>
              )}
            </div>
            <div className={styles.divider}>
              <span className={styles.dividerText}>or</span>
            </div>
          </>
        )}

        <form onSubmit={handleSubmit} className={styles.registerForm}>
          <div className={styles.formGroup}>
            <label htmlFor="firstName" className={styles.label}>
              First Name
            </label>
            <input
              id="firstName"
              type="text"
              value={formData.firstName}
              onChange={(e) => onFieldChange('firstName', e.target.value)}
              className={`${styles.input} ${errors.firstName ? styles.inputError : ''}`}
              placeholder="John"
              required
              autoComplete="given-name"
            />
            {errors.firstName && (
              <span className={styles.errorMessage}>{errors.firstName}</span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="lastName" className={styles.label}>
              Last Name
            </label>
            <input
              id="lastName"
              type="text"
              value={formData.lastName}
              onChange={(e) => onFieldChange('lastName', e.target.value)}
              className={`${styles.input} ${errors.lastName ? styles.inputError : ''}`}
              placeholder="Doe"
              required
              autoComplete="family-name"
            />
            {errors.lastName && (
              <span className={styles.errorMessage}>{errors.lastName}</span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="companyName" className={styles.label}>
              Company Name <span className={styles.optionalLabel}>(Optional)</span>
            </label>
            <input
              id="companyName"
              type="text"
              value={formData.companyName}
              onChange={(e) => onFieldChange('companyName', e.target.value)}
              className={`${styles.input} ${errors.companyName ? styles.inputError : ''}`}
              placeholder="Acme Corp"
              autoComplete="organization"
            />
            {errors.companyName && (
              <span className={styles.errorMessage}>{errors.companyName}</span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.label}>
              Email
            </label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => onFieldChange('email', e.target.value)}
              className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
              placeholder="john@example.com"
              required
              autoComplete="email"
            />
            {errors.email && (
              <span className={styles.errorMessage}>{errors.email}</span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="password" className={styles.label}>
              Password
            </label>
            <div className={styles.passwordWrapper}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
                placeholder="Enter your password"
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={styles.passwordToggle}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            {formData.password && (
              <div className={styles.passwordStrength}>
                <div className={styles.strengthBar}>
                  <div 
                    className={styles.strengthFill}
                    style={{
                      width: `${passwordStrength * 25}%`,
                      backgroundColor: getPasswordStrengthColor()
                    }}
                  />
                </div>
                <span className={styles.strengthLabel} style={{ color: getPasswordStrengthColor() }}>
                  {getPasswordStrengthLabel()}
                </span>
              </div>
            )}
            {errors.password && (
              <span className={styles.errorMessage}>{errors.password}</span>
            )}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="confirmPassword" className={styles.label}>
              Confirm Password
            </label>
            <div className={styles.passwordWrapper}>
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => onFieldChange('confirmPassword', e.target.value)}
                className={`${styles.input} ${errors.confirmPassword ? styles.inputError : ''}`}
                placeholder="Confirm your password"
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className={styles.passwordToggle}
              >
                {showConfirmPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <span className={styles.errorMessage}>{errors.confirmPassword}</span>
            )}
          </div>

          {/* Terms of Service & Privacy Policy (includes AI disclaimer language) */}
          <div className={styles.checkboxLabel}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.acceptTerms}
                onChange={(e) => onFieldChange('acceptTerms', e.target.checked)}
                className={styles.checkbox}
                required
              />
              <span className={styles.checkboxText}>
                I agree to the <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className={styles.link}>Terms of Service</a> and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className={styles.link}>Privacy Policy</a>, including the AI-assisted
                tool disclaimer and liability waiver
              </span>
            </label>
          </div>

          {/* T_REMOVED_NEWSLETTER_2026_01_02: Removed product updates checkbox to simplify form */}

          {errors.general && (
            <div className={styles.errorMessage}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span>{errors.general}</span>
            </div>
          )}

          <button
            type="submit"
            className={styles.submitButton}
            disabled={isLoading || !formData.acceptTerms}
          >
            {isLoading ? (
              <>
                <span className={styles.spinner}></span>
                Creating Account...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        <div className={styles.footer}>
          <p className={styles.footerText}>
            Already have an account?{' '}
            <button
              type="button"
              onClick={onLoginClick}
              className={styles.link}
            >
              Sign in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Pea_Lobby_Register_SRC_Auth_Customer_Kings;